import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Forecast } from "@/lib/api";
import { Printer, ArrowLeft, ArrowUpRight, ArrowDownRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BillyMark } from "@/components/BillyLogo";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const WeeklyForecast = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Forecast.threeMonth()
      .then(setData)
      .catch(() => toast.error("Could not load forecast."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (!data) return <div className="text-sm text-neutral-500">No data.</div>;

  const today = new Date(data.today).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const s = data.summary;
  const runningStart = 0; // starting cash position (user can mentally adjust)
  let running = runningStart;
  const monthsWithBalance = data.months.map((m) => {
    running += m.net;
    return { ...m, running: Math.round(running * 100) / 100 };
  });

  return (
    <div data-testid="weekly-forecast" className="bg-white text-black min-h-screen">
      {/* Action bar — hidden on print */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/weekly")}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Weekly
          </button>
          <button
            data-testid="print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 print:p-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 border-b-2 border-black pb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              3-Month Cash-Flow Forecast
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight mt-1">
              The Outdoor Blind Company
            </h1>
            <div className="text-sm text-neutral-700 mt-2 mono">
              Prepared {today} · 3 months historical / 3 months projected
            </div>
          </div>
          <BillyMark size={56} />
        </div>

        {/* Summary KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="summary-tiles">
          <Tile label="Last 3 mo. IN" value={fmt(s.history_total_ins)} accent="green" />
          <Tile label="Last 3 mo. OUT" value={fmt(s.history_total_outs)} accent="amber" />
          <Tile label="Next 3 mo. IN" value={fmt(s.forecast_total_ins)} accent="green" />
          <Tile label="Next 3 mo. OUT" value={fmt(s.forecast_total_outs)} accent="amber" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <Tile
            label="Historical net"
            value={fmt(s.history_net)}
            accent={s.history_net >= 0 ? "green" : "red"}
          />
          <Tile
            label="Forecast net"
            value={fmt(s.forecast_net)}
            accent={s.forecast_net >= 0 ? "green" : "red"}
          />
          <Tile
            label={`Overdue (${s.overdue_count})`}
            value={fmt(s.overdue_total)}
            accent="red"
            icon={<AlertTriangle className="h-4 w-4" />}
          />
        </div>

        {/* Monthly table */}
        <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
          Monthly cash-flow
        </h2>
        <table className="w-full mt-4 text-sm" data-testid="month-table">
          <thead>
            <tr className="text-left border-b border-neutral-300">
              <th className="py-2 swiss-label">Month</th>
              <th className="py-2 swiss-label">Type</th>
              <th className="py-2 swiss-label text-right">IN</th>
              <th className="py-2 swiss-label text-right">OUT</th>
              <th className="py-2 swiss-label text-right">Net</th>
              <th className="py-2 swiss-label text-right">Running</th>
            </tr>
          </thead>
          <tbody>
            {monthsWithBalance.map((m) => (
              <tr
                key={m.label}
                className={`border-b border-neutral-100 ${m.kind === "forecast" ? "bg-neutral-50" : ""}`}
              >
                <td className="py-2 font-bold">{m.label}</td>
                <td className="py-2 text-[10px] uppercase tracking-widest">
                  {m.kind === "history" ? "Actual" : "Projected"}
                </td>
                <td className="py-2 mono text-right text-green-700">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <ArrowUpRight className="h-3 w-3" />
                    {fmt(m.ins)}
                  </span>
                </td>
                <td className="py-2 mono text-right text-amber-700">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <ArrowDownRight className="h-3 w-3" />
                    {fmt(m.outs)}
                  </span>
                </td>
                <td
                  className={`py-2 mono text-right font-bold ${m.net >= 0 ? "text-green-700" : "text-red-600"}`}
                >
                  {fmt(m.net)}
                </td>
                <td
                  className={`py-2 mono text-right font-bold ${m.running >= 0 ? "text-green-700" : "text-red-600"}`}
                >
                  {fmt(m.running)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-[11px] text-neutral-500 mt-2">
          Running balance assumes a $0.00 starting cash position. Add your bank balance
          mentally to project actual cash.
        </div>

        {/* Future incoming */}
        <ItemSection title="Expected income — next 3 months" groups={data.future_incomes} kind="ins" />

        {/* Future outgoing */}
        <ItemSection title="Bills due — next 3 months" groups={data.future_bills} kind="outs" />

        {/* Overdue list */}
        {data.overdue_bills.length > 0 && (
          <>
            <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2 text-red-600">
              Overdue bills · {fmt(s.overdue_total)}
            </h2>
            <table className="w-full mt-4 text-sm" data-testid="overdue-table">
              <thead>
                <tr className="text-left border-b border-neutral-300">
                  <th className="py-2 swiss-label">Due</th>
                  <th className="py-2 swiss-label">Company</th>
                  <th className="py-2 swiss-label">Invoice #</th>
                  <th className="py-2 swiss-label text-right">Owing</th>
                </tr>
              </thead>
              <tbody>
                {data.overdue_bills.map((b) => (
                  <tr key={b.id} className="border-b border-neutral-100">
                    <td className="py-2 mono">{b.due_date}</td>
                    <td className="py-2 font-bold">{b.company}</td>
                    <td className="py-2 mono">{b.invoice_number}</td>
                    <td className="py-2 mono text-right text-red-600 font-bold">
                      {fmt(b.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="mt-12 pt-6 border-t-2 border-black text-[11px] uppercase tracking-[0.25em] text-neutral-500 text-center">
          Generated by Billy · The Outdoor Blind Company · {today}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 16mm; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          table { page-break-inside: avoid; }
          h2 { page-break-after: avoid; }
        }
      `}</style>
    </div>
  );
};

const Tile = ({ label, value, accent, icon }) => {
  const colors = {
    green: "border-black",
    amber: "border-black",
    red: "border-red-600 bg-red-50",
  };
  const vColors = {
    green: "text-green-700",
    amber: "text-amber-700",
    red: "text-red-600",
  };
  return (
    <div className={`border-2 p-5 bg-white ${colors[accent] || "border-black"}`}>
      <div className="swiss-label inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div
        className={`font-display font-black text-2xl mono mt-1 ${vColors[accent] || ""}`}
      >
        {value}
      </div>
    </div>
  );
};

const ItemSection = ({ title, groups, kind }) => {
  const anyItems = groups.some((g) => g.items.length > 0);
  if (!anyItems) {
    return (
      <>
        <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
          {title}
        </h2>
        <div className="text-sm text-neutral-500 mt-4 italic">
          Nothing scheduled.
        </div>
      </>
    );
  }
  return (
    <>
      <h2 className="font-display font-extrabold text-xl uppercase tracking-widest mt-10 border-b-2 border-black pb-2">
        {title}
      </h2>
      {groups.map((g, i) => (
        <div key={i} className="mt-5" data-testid={`itemgroup-${kind}-${i}`}>
          <div className="swiss-label mb-2">{g.month}</div>
          {g.items.length === 0 ? (
            <div className="text-[11px] text-neutral-500 italic">
              Nothing scheduled.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-neutral-300">
                  <th className="py-2 swiss-label">Date</th>
                  <th className="py-2 swiss-label">
                    {kind === "ins" ? "Customer / Category" : "Vendor / Invoice"}
                  </th>
                  <th className="py-2 swiss-label text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => (
                  <tr key={it.id} className="border-b border-neutral-100">
                    <td className="py-2 mono">
                      {it.expected_date || it.due_date}
                    </td>
                    <td className="py-2">
                      <div className="font-bold">
                        {it.client_name || it.company}
                      </div>
                      <div className="text-[11px] text-neutral-500">
                        {it.category || it.invoice_number || ""}
                      </div>
                    </td>
                    <td
                      className={`py-2 mono text-right font-bold ${kind === "ins" ? "text-green-700" : "text-amber-700"}`}
                    >
                      {fmt(it.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-black">
                  <td colSpan={2} className="py-2 swiss-label">
                    Subtotal
                  </td>
                  <td
                    className={`py-2 mono text-right font-bold ${kind === "ins" ? "text-green-700" : "text-amber-700"}`}
                  >
                    {fmt(g.items.reduce((s, x) => s + Number(x.amount || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      ))}
    </>
  );
};

export default WeeklyForecast;
